import React from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Link,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import BusinessRating from '../../../components/BusinessRating';
import { InfoBadge } from '../../../components/InfoBadge';
import { Business, ShippingPolicy } from '../../../data/types/domain';
import { MinimumOrderType } from '../../../data/types/domain';
import styled from '@emotion/styled';
import { Theme } from '@mui/material/styles';

interface ComparisonCompanyCardProps {
  business: Business;
  logoURL: string;
  shippingPolicy?: ShippingPolicy | null;
  isDark: boolean;
  onBusinessClick: (id: number, name: string) => void;
}

const CompanyCard = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  background-color: ${props => props.theme.palette.background.paper};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const ContentWrapper = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const CompanyLogo = styled(Box)<{ isDark: boolean; theme: Theme }>`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: ${props => props.isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 0.75rem;
  flex-shrink: 0;
  margin: 0 auto;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    margin: 0;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const CompanyDetails = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  justify-content: flex-start;

  .company-name {
    font-size: 1.25rem;
    font-weight: 600;
  }

  .company-description {
    font-size: 0.875rem;
    color: ${props => props.theme.palette.text.secondary};
    margin-bottom: 0.5rem;
  }
`;

const ViewDetailsButton = styled(Link)<{ theme: Theme }>`
  text-decoration: none;
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background-color: ${props => props.theme.palette.primary.main};
  color: white;
  text-align: center;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  align-self: center;
  margin-top: auto;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    background-color: ${props => props.theme.palette.primary.dark};
  }
`;

const ActionBar = styled(Box)<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(
    to left,
    ${props => props.theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.03)' 
      : 'white'},
    ${props => props.theme.palette.mode === 'dark' 
      ? 'rgba(240, 248, 255, 0.05)' 
      : 'aliceblue'}
  );
  border-top: 1px solid ${props => props.theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.08)' 
    : 'rgba(0, 0, 0, 0.06)'};
  backdrop-filter: blur(8px);

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  @media (max-width: ${props => props.theme.breakpoints.values.sm}px) {
    padding: 0.875rem 1.5rem;
  }
`;

const ActionButton = styled(Link)<{ theme: Theme }>`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  background: linear-gradient(
    135deg,
    ${props => props.theme.palette.primary.main},
    ${props => props.theme.palette.primary.dark}
  );
  color: white;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-width: 120px;
  letter-spacing: 0.01em;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    flex: 0 1 auto;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    background: linear-gradient(
      135deg,
      ${props => props.theme.palette.primary.dark},
      ${props => props.theme.palette.primary.main}
    );

    &::before {
      transform: translateX(100%);
    }
  }
`;

const SkeletonLogo = styled(Skeleton)<{ theme: Theme }>`
  width: 120px;
  height: 120px;
  border-radius: 0.75rem;
  flex-shrink: 0;
  margin: 0 auto;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    margin: 0;
  }
`;

const SkeletonText = styled(Skeleton)<{ theme: Theme }>`
  border-radius: 0.25rem;
`;

const SkeletonButton = styled(Skeleton)<{ theme: Theme }>`
  border-radius: 0.5rem;
  flex: 1;
  min-width: 120px;
  height: 36px;

  @media (min-width: ${props => props.theme.breakpoints.values.sm}px) {
    flex: 0 1 auto;
  }
`;

const SocialMediaButton = styled(IconButton)<{ theme: Theme }>`
  padding: 0.375rem;
  color: ${props => props.theme.palette.text.secondary};
  transition: all 0.2s ease;
  background: ${props => props.theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.08)'};
  
  & .MuiSvgIcon-root {
    font-size: 1.25rem;
  }
  
  &:hover {
    transform: translateY(-1px);
    background: ${props => props.theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.04)'};
    color: ${props => props.theme.palette.primary.main};
  }
`;

export const ComparisonCompanyCard: React.FC<ComparisonCompanyCardProps> = ({
  business,
  logoURL,
  shippingPolicy,
  isDark,
  onBusinessClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const socialLinks = [
    { url: business.facebook, icon: <FacebookIcon />, label: 'Facebook' },
    { url: business.instagram, icon: <InstagramIcon />, label: 'Instagram' },
    { url: business.twitter, icon: <TwitterIcon />, label: 'Twitter' },
    { url: business.youtube, icon: <YouTubeIcon />, label: 'YouTube' },
  ].filter(link => link.url);

  return (
    <CompanyCard
      component="article"
      aria-labelledby={`company-${business.id}-name`}
      theme={theme}
    >
      <ContentWrapper theme={theme}>
        <CompanyLogo isDark={isDark} theme={theme}>
          {logoURL ? (
            <img
              src={logoURL}
              alt={`${business.name} logo`}
              loading="lazy"
            />
          ) : (
            <Box
              sx={{
                width: '75%',
                height: '75%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
                borderRadius: '8px',
                color: theme.palette.text.secondary,
                fontSize: '1.5rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                userSelect: 'none',
              }}
            >
              {business.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
            </Box>
          )}
        </CompanyLogo>

        <CompanyDetails theme={theme}>
          <Typography
            className="company-name"
            id={`company-${business.id}-name`}
          >
            {business.name}
          </Typography>
          <Typography className="company-description">
            {business.description}
          </Typography>
          {shippingPolicy && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              mb: 1,
            }}>
              {shippingPolicy.minimum_order_amount === 0 ? (
                <InfoBadge 
                  icon={<CheckCircleOutlineIcon sx={{ fontSize: '0.875rem', opacity: 0.8, color: 'inherit' }} />}
                  isDark={isDark}
                  variant="success"
                >
                  No minimum order
                </InfoBadge>
              ) : (
                <InfoBadge 
                  icon="💷"
                  isDark={isDark}
                  variant="info"
                >
                  {shippingPolicy.minimum_order_type === MinimumOrderType.PER_KG
                    ? 'From '
                    : 'Min. '}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: 'inherit',
                    }}
                  >
                    £{shippingPolicy.minimum_order_amount?.toFixed(2) || '0.00'}
                  </Typography>
                  {shippingPolicy.minimum_order_type === MinimumOrderType.PER_KG ? ' per kg' : ' order'}
                </InfoBadge>
              )}
            </Box>
          )}
          {(business.trustpilot_rating || business.google_rating) && (
            <Box className="rating-container" sx={{ display: 'flex', alignItems: 'end', gap: 2 }}>
              {isMobile ? (
                business.trustpilot_rating ? (
                  <BusinessRating
                    rating={business.trustpilot_rating}
                    reviewsCount={business.trustpilot_reviews_count || 0}
                    reviewsUrl={business.trustpilot_reviews_url}
                    source="trustpilot"
                    isMobile={isMobile}
                  />
                ) : business.google_rating ? (
                  <BusinessRating
                    rating={business.google_rating}
                    reviewsCount={business.google_reviews_count || 0}
                    reviewsUrl={business.google_reviews_url}
                    source="google"
                    isMobile={isMobile}
                  />
                ) : null
              ) : (
                <>
                  {business.trustpilot_rating && (
                    <BusinessRating
                      rating={business.trustpilot_rating}
                      reviewsCount={business.trustpilot_reviews_count || 0}
                      reviewsUrl={business.trustpilot_reviews_url}
                      source="trustpilot"
                      isMobile={isMobile}
                    />
                  )}
                  {business.google_rating && (
                    <BusinessRating
                      rating={business.google_rating}
                      reviewsCount={business.google_reviews_count || 0}
                      reviewsUrl={business.google_reviews_url}
                      source="google"
                      isMobile={isMobile}
                    />
                  )}
                </>
              )}
            </Box>
          )}
        </CompanyDetails>
      </ContentWrapper>

      <ActionBar theme={theme}>
        {socialLinks.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5,
            justifyContent: 'center',
            '@media (min-width: 600px)': {
              mr: 'auto',
              justifyContent: 'flex-start'
            }
          }}>
            {socialLinks.map((link, index) => (
              <Tooltip key={index} title={link.label}>
                <Link
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit ${business.name}'s ${link.label}`}
                  sx={{ textDecoration: 'none' }}
                >
                  <SocialMediaButton theme={theme}>
                    {link.icon}
                  </SocialMediaButton>
                </Link>
              </Tooltip>
            ))}
          </Box>
        )}
        <ActionButton
          href={business.website || '#'}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View site for ${business.name}`}
          onClick={() => {
            onBusinessClick(business.id, business.name);
          }}
          theme={theme}
        >
          View Website
        </ActionButton>
      </ActionBar>
    </CompanyCard>
  );
}; 